/**
 * Promise-based confirmation dialog.
 *
 * Replaces `window.confirm` and `window.alert` so that destructive actions
 * use a styled in-app dialog instead of the native browser popup at the
 * top of the screen.
 *
 * Usage:
 *   const confirm = useConfirm();
 *
 *   // Yes/no destructive (default cancelable, with a red confirm button)
 *   const ok = await confirm({
 *     title: 'Delete watchlist?',
 *     message: '"My Watchlist" will be permanently deleted.',
 *     confirmLabel: 'Delete',
 *     confirmVariant: 'danger',
 *   });
 *   if (!ok) return;
 *
 *   // Information-only (single OK)
 *   await confirm({ title: 'Saved', message: 'Changes saved.', cancelLabel: null });
 *
 *   // Three-way choice (returns the chosen value, or null on dismiss)
 *   const choice = await confirm({
 *     title: 'Unsaved changes',
 *     message: 'You have unsaved changes.',
 *     choices: [
 *       { value: 'save', label: 'Save', variant: 'primary' },
 *       { value: 'discard', label: 'Discard', variant: 'danger' },
 *       { value: 'cancel', label: 'Cancel', variant: 'ghost' },
 *     ],
 *   });
 *
 * Behavior:
 *   - Escape / overlay-click → resolves as `false` (yes/no form) or `null`
 *     (choices form). Set `dismissible: false` to disable.
 *   - Only one dialog open at a time. A second confirm() call while another
 *     is open queues it; they resolve in FIFO order.
 *   - The dialog never appears at the top of the browser chrome. It uses
 *     the same Radix Dialog primitive as <Modal>.
 */
import * as Dialog from '@radix-ui/react-dialog';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import { Button, type ButtonVariant } from './Button';
import { Input } from './Input';

// ---- API types --------------------------------------------------------------

export interface ConfirmChoice<TValue = string> {
  value: TValue;
  label: string;
  variant?: ButtonVariant;
}

export interface ConfirmOptionsYesNo {
  title?: ReactNode;
  message?: ReactNode;
  confirmLabel?: string;            // default "OK"
  confirmVariant?: ButtonVariant;   // default "primary"
  cancelLabel?: string | null;      // null = hide cancel button (info-only)
  dismissible?: boolean;            // overlay/Escape closes. Default true.
}

export interface ConfirmOptionsChoices<TValue = string> {
  title?: ReactNode;
  message?: ReactNode;
  choices: ConfirmChoice<TValue>[];
  dismissible?: boolean;            // default true
}

export interface ConfirmOptionsPrompt {
  title?: ReactNode;
  message?: ReactNode;
  prompt: {
    label?: string;
    placeholder?: string;
    initialValue?: string;
    type?: 'text' | 'password' | 'email' | 'number';
    required?: boolean;             // default true — disables submit while empty
  };
  confirmLabel?: string;            // default "OK"
  confirmVariant?: ButtonVariant;   // default "primary"
  cancelLabel?: string | null;      // null = hide cancel button
  dismissible?: boolean;            // default true
}

export type ConfirmOptions<TValue = string> =
  | ConfirmOptionsYesNo
  | ConfirmOptionsChoices<TValue>
  | ConfirmOptionsPrompt;

// Three overloads of the returned function so callers get the right return type.
export interface ConfirmFn {
  (options: ConfirmOptionsYesNo): Promise<boolean>;
  (options: ConfirmOptionsPrompt): Promise<string | null>;
  <TValue extends string>(options: ConfirmOptionsChoices<TValue>): Promise<TValue | null>;
}

// ---- Provider + hook --------------------------------------------------------

interface QueuedDialog {
  id: number;
  options: ConfirmOptions<string>;
  resolve: (value: boolean | string | null) => void;
}

const ConfirmContext = createContext<ConfirmFn | null>(null);

export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used inside <ConfirmProvider>');
  return ctx;
}

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [queue, setQueue] = useState<QueuedDialog[]>([]);
  const nextIdRef = useRef(1);

  const confirm = useCallback(
    (options: ConfirmOptions<string>) =>
      new Promise<boolean | string | null>((resolve) => {
        const id = nextIdRef.current++;
        setQueue((q) => [...q, { id, options, resolve }]);
      }),
    [],
  ) as unknown as ConfirmFn;

  const current = queue[0];

  const closeCurrent = useCallback(
    (value: boolean | string | null) => {
      if (!current) return;
      current.resolve(value);
      setQueue((q) => q.slice(1));
    },
    [current],
  );

  const value = useMemo(() => confirm, [confirm]);

  return (
    <ConfirmContext.Provider value={value}>
      {children}
      {current && <ConfirmDialog dialog={current} onClose={closeCurrent} />}
    </ConfirmContext.Provider>
  );
}

// ---- The dialog itself ------------------------------------------------------

function ConfirmDialog({
  dialog,
  onClose,
}: {
  dialog: QueuedDialog;
  onClose: (value: boolean | string | null) => void;
}) {
  const { options } = dialog;
  const isChoices = 'choices' in options;
  const isPrompt = 'prompt' in options;
  const dismissible = options.dismissible !== false;
  const dismissValue: boolean | string | null = isChoices || isPrompt ? null : false;

  return (
    <Dialog.Root
      open
      onOpenChange={(open) => {
        if (!open && dismissible) onClose(dismissValue);
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay
          className="modal__overlay"
          onClick={(e) => {
            // Radix already handles this via onOpenChange when dismissible.
            // We just prevent the default-action edge case where the click
            // also hits the content.
            if (!dismissible) e.preventDefault();
          }}
        />
        <Dialog.Content
          className="modal__content"
          onEscapeKeyDown={(e) => {
            if (!dismissible) e.preventDefault();
          }}
          onPointerDownOutside={(e) => {
            if (!dismissible) e.preventDefault();
          }}
        >
          {options.title && (
            <Dialog.Title className="modal__title">{options.title}</Dialog.Title>
          )}
          {options.message && (
            <Dialog.Description className="modal__description">
              {options.message}
            </Dialog.Description>
          )}
          {isPrompt ? (
            <PromptForm
              options={options as ConfirmOptionsPrompt}
              onSubmit={(v) => onClose(v)}
              onCancel={() => onClose(null)}
            />
          ) : (
            <div className="modal__footer">
              {isChoices ? (
                <ChoiceButtons
                  choices={(options as ConfirmOptionsChoices).choices}
                  onPick={(v) => onClose(v)}
                />
              ) : (
                <YesNoButtons
                  options={options as ConfirmOptionsYesNo}
                  onConfirm={() => onClose(true)}
                  onCancel={() => onClose(false)}
                />
              )}
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function YesNoButtons({
  options,
  onConfirm,
  onCancel,
}: {
  options: ConfirmOptionsYesNo;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const showCancel = options.cancelLabel !== null;
  return (
    <>
      {showCancel && (
        <Button variant="ghost" onClick={onCancel}>
          {options.cancelLabel ?? 'Cancel'}
        </Button>
      )}
      <Button
        variant={options.confirmVariant ?? 'primary'}
        onClick={onConfirm}
        autoFocus
      >
        {options.confirmLabel ?? 'OK'}
      </Button>
    </>
  );
}

function PromptForm({
  options,
  onSubmit,
  onCancel,
}: {
  options: ConfirmOptionsPrompt;
  onSubmit: (value: string) => void;
  onCancel: () => void;
}) {
  const [value, setValue] = useState(options.prompt.initialValue ?? '');
  const required = options.prompt.required !== false;
  const showCancel = options.cancelLabel !== null;
  const disabled = required && value.trim() === '';

  return (
    <form
      className="stack-5"
      onSubmit={(e) => {
        e.preventDefault();
        if (!disabled) onSubmit(value);
      }}
    >
      <Input
        label={options.prompt.label}
        placeholder={options.prompt.placeholder}
        type={options.prompt.type ?? 'text'}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        autoFocus
      />
      <div className="modal__footer">
        {showCancel && (
          <Button type="button" variant="ghost" onClick={onCancel}>
            {options.cancelLabel ?? 'Cancel'}
          </Button>
        )}
        <Button
          type="submit"
          variant={options.confirmVariant ?? 'primary'}
          disabled={disabled}
        >
          {options.confirmLabel ?? 'OK'}
        </Button>
      </div>
    </form>
  );
}

function ChoiceButtons({
  choices,
  onPick,
}: {
  choices: ConfirmChoice[];
  onPick: (value: string) => void;
}) {
  return (
    <>
      {choices.map((c, i) => (
        <Button
          key={c.value}
          variant={c.variant ?? 'ghost'}
          onClick={() => onPick(c.value)}
          autoFocus={i === 0}
        >
          {c.label}
        </Button>
      ))}
    </>
  );
}
